import type { AuthMethod } from '@/features/tools/types'
import {
  AUTH_PERSON_LABELS,
  type AuthPerson,
} from '@/features/tools/components/authenticate-caller/authConstants'

// ---- Types ----

/** UI-internal method row. `n === 'all'` -> all fields must match. */
export type MethodRow = { fields: string[]; n: number | 'all' }

export type DraftState = {
  methods: MethodRow[]
  allowedPersons: string[]
}

// ---- Constants ----
export const ORG_DEFAULT_LABEL = "Inherited from your organization's default"
export const RESET_TO_ORG_DEFAULT_LABEL = "Reset to your organization's default"

// ---- Helpers ----

/** Compare two persons arrays as sets (order-insensitive). */
export function personsEqual(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false
  return [...a].toSorted().join('\0') === [...b].toSorted().join('\0')
}

/**
 * Compare two method-row arrays. Fields within each row are compared as a
 * sorted set so chip-reorder isn't a false diff. Empty rows (no fields added
 * yet) are ignored so an unfilled "+ Add method" click doesn't register as a
 * change.
 */
export function methodsEqual(a: MethodRow[], b: MethodRow[]): boolean {
  const aa = a.filter((r) => r.fields.length > 0)
  const bb = b.filter((r) => r.fields.length > 0)
  if (aa.length !== bb.length) return false
  return aa.every((rowA, i) => {
    // bb[i] is defined because we checked aa.length === bb.length above.
    const rowB = bb[i]!
    return (
      rowA.n === rowB.n &&
      [...rowA.fields].toSorted().join('\0') ===
        [...rowB.fields].toSorted().join('\0')
    )
  })
}

/** Convert an API AuthMethod into the UI row shape. */
export function authMethodToRow(method: AuthMethod): MethodRow {
  if (Array.isArray(method)) {
    return { fields: method, n: 'all' }
  }
  if (method && typeof method === 'object' && 'n_of' in method) {
    return { fields: method.n_of.fields, n: method.n_of.n }
  }
  return { fields: [], n: 'all' }
}

/** Convert a UI row back into the API AuthMethod shape. */
export function rowToAuthMethod(row: MethodRow): AuthMethod {
  if (row.n === 'all') return row.fields
  return { n_of: { n: row.n, fields: row.fields } }
}

export function buildDraftFromSettings(
  settings: { methods: AuthMethod[]; allowed_persons: string[] } | undefined
): DraftState {
  return {
    methods: (settings?.methods ?? []).map(authMethodToRow),
    allowedPersons: settings?.allowed_persons ?? [],
  }
}

/** Format an ad-hoc person key as a human-readable label. */
export function formatPersonLabel(person: string): string {
  if (person in AUTH_PERSON_LABELS) {
    return AUTH_PERSON_LABELS[person as AuthPerson]
  }
  return person
    .split('_')
    .map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1) : w))
    .join(' ')
}
